import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createAIModuleRequest } from 'core/services/admin';

/**
 * Tracks an AI module generation in flight.
 *
 * `POST /api/admin/ai-module` is fire-and-forget: it flips the AI module row to
 * BUILD, puts the job on the bus and returns immediately, so the thunk
 * resolving means "accepted", not "generated". Nothing polls, either — the
 * READY stage is never written server-side, so a status GET would spin forever.
 *
 * The only real completion signal is the websocket message the jobs worker
 * sends once every slide exists: a `ResponseModel` with `type: MODULE`,
 * `method: UPDATE` carrying the finished module. The socket saga already merges
 * that into the entity store; this slice is what notices it happened.
 *
 * Scoping is what makes an ordinary module update usable as the signal: we only
 * listen while a generation we started is outstanding, and only for the module
 * it targets. Outside that window the same message means nothing to us.
 */
export enum AIModuleState {
  IDLE = 'idle',
  /** Queued but not yet accepted — the POST is in flight. */
  SUBMITTING = 'submitting',
  /** Accepted and on the bus. No further progress is reported until it lands. */
  BUILDING = 'building',
  READY = 'ready',
  FAILED = 'failed',
}

export interface IAIModuleReducer {
  /** The module being generated into, not the AI module row. */
  moduleId: number | null;
  state: AIModuleState;
  error: string | null;
}

const initialState: IAIModuleReducer = {
  moduleId: null,
  state: AIModuleState.IDLE,
  error: null,
};

const aiModuleSlice = createSlice({
  name: 'aiModule',
  initialState,
  reducers: {
    /** Dispatched by the socket saga when the finished module arrives. */
    generationComplete: (state, action: PayloadAction<{ moduleId: number }>) => {
      if (state.moduleId !== action.payload.moduleId) return;
      state.state = AIModuleState.READY;
    },
    /** Leaving the form, or acting on a finished module, clears the watch. */
    reset: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(createAIModuleRequest.pending, (state, action) => {
        state.moduleId = action.meta.arg.moduleId ?? null;
        state.state = AIModuleState.SUBMITTING;
        state.error = null;
      })
      .addCase(createAIModuleRequest.fulfilled, (state, action) => {
        // The response echoes the request with ModuleId backfilled from the AI
        // module row, which is the only place the target id is authoritative.
        const moduleId = action.payload?.moduleId ?? state.moduleId;
        state.moduleId = moduleId ?? null;
        state.state = AIModuleState.BUILDING;
      })
      .addCase(createAIModuleRequest.rejected, (state, action) => {
        state.state = AIModuleState.FAILED;
        state.error = action.error?.message ?? 'Could not start generation.';
      });
  },
});

export const { generationComplete, reset } = aiModuleSlice.actions;

export default aiModuleSlice.reducer;
